import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation
} from "react-router-dom";
import BottomNav from "./components/BottomNav";
import { Toaster } from "@/components/ui/toaster";
import { Analytics } from "./components/Analytics";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import Index from "./pages/Index";
import Profile from "./pages/Profile";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import NewTrip from "./pages/NewTrip";
import Assistant from "./pages/Assistant";
import Discover from "./pages/Discover";
import { config } from './lib/hey/config';
import TripTimelinePage from "./pages/TripTimelinePage";
import { I18nextProvider } from "react-i18next";
import i18n from "@/i18n";
import { HeyUtils } from "./lib/hey/core";
import { GoogleAdsScriptLoader } from "@/components/ads/GoogleAdsScriptLoader";
import { useFeatureFlagStatus } from "@/utils/FeatureFlagProvider";
import { getAdSenseConfig } from "./constants/adsense";
import { FeatureFlagProvider } from "@/utils/FeatureFlagProvider";

const queryClient = new QueryClient();

// Move ADTD query URL into localStorage
if (location.search) {
  const urlParams = new URLSearchParams(location.search);
  // PNR Import
  if (urlParams.has("E") && urlParams.has("F") && urlParams.has("N") && urlParams.has("R")) {
    const atdtData = {
      encryptedEmail: urlParams.get("E"),
      encryptedFirstName: urlParams.get("F"),
      encryptedLastName: urlParams.get("N"),
      encryptedRecloc: urlParams.get("R")
    }
    localStorage.setItem("atdtData", JSON.stringify(atdtData));
    urlParams.delete("E");
    urlParams.delete("F");
    urlParams.delete("N");
    urlParams.delete("R");
    history.replaceState({}, document.title, location.pathname + "?" + urlParams.toString());
  }

  // OTP Validation
  if (urlParams.has("userEmail") && urlParams.has("code")) {
    const userEmail = urlParams.get("userEmail");
    const code = urlParams.get("code");
    console.log("User Email:", userEmail);
    console.log("Code:", code);
    const currentUserToken = HeyUtils.getInstance().userAccessToken
    localStorage.setItem("otp_userEmail", userEmail);
    localStorage.setItem("otp_code", code);
    // Redirect to Main page if code has been handled
    setInterval(() => {
      const userLocalToken = localStorage.getItem(config.LS_KEY_TOKEN);
      if (userLocalToken && userLocalToken !== currentUserToken) {
        console.log("User is authenticated");
        clearInterval(this);
      }
    }, 500);
  }
}
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}



function AnimatedRoutes() {
  const location = useLocation();
  return (
    <>
         <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Index />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/new-trip" element={<NewTrip />} />
          <Route path="/assistant" element={<Assistant />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/trip-timeline" element={<TripTimelinePage />} />
          <Route path="/*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
    </>
  );
}

function AppWithRouterLogic() {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [publisherId, setPublisherId] = useState<string | null>(null);
  const { googleAds: googleAdsEnabled } = useFeatureFlagStatus();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const { publisherId } = getAdSenseConfig('728_90_Responsive');
    if (publisherId) {
      setPublisherId(publisherId);
      setConfigLoaded(true);
      console.log('[App] AdSense config loaded:', publisherId);
    } else {
      console.log('[App] AdSense config NOT loaded');
    }
  }, []);

  useEffect(() => {
    // Check authentication status on mount and on route change
    setIsAuthenticated(HeyUtils.getInstance().isUserAuthenticated());
  }, [location.pathname]);

  useEffect(() => {
    console.log('[App] Google Ads flag loaded:', googleAdsEnabled);
  }, [googleAdsEnabled]);

  // Only show AdSense script loader if authenticated and not on / or /auth
  const shouldLoadAdsScript =
    configLoaded &&
    publisherId &&
    googleAdsEnabled === true &&
    isAuthenticated &&
    location.pathname !== '/auth';

  return (
    <>
      <Analytics />
      {shouldLoadAdsScript && (
        <GoogleAdsScriptLoader enabled={googleAdsEnabled} publisherId={publisherId} />
      )}
      <ScrollToTop />
      <main>
        <AnimatedRoutes />
        <BottomNav />
      </main>
      <Toaster />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <Router>
          <AppWithRouterLogic />
        </Router>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

function AppRoot() {
  return (
    <FeatureFlagProvider>
      <App />
    </FeatureFlagProvider>
  );
}

export default AppRoot;
